import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["United Kingdom", "Canada", "Ireland", "United States", "Australia", "India", "Other"];

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", email: "", password: "", dob: "", country: "United Kingdom", province: "",
  });
  const [consents, setConsents] = useState({ own_work: false, no_writing: false, integrity: false, terms: false, marketing: false });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const setC = (k) => (v) => setConsents((c) => ({ ...c, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register({ ...form, consents });
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created — 20 free tokens added.");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const allAgreed = consents.own_work && consents.no_writing && consents.integrity && consents.terms;
  const setAllAgreed = (v) => setConsents((c) => ({ ...c, own_work: !!v, no_writing: !!v, integrity: !!v, terms: !!v }));

  return (
    <div className="flex min-h-screen bg-background">
      <div className="hidden w-5/12 flex-col justify-between integrity-gradient p-12 text-white lg:flex">
        <Logo onDark />
        <div>
          <ShieldCheck className="mb-5 h-10 w-10" />
          <h2 className="font-display text-3xl font-bold leading-tight">Your work stays yours.</h2>
          <p className="mt-4 max-w-sm text-white/85">ThinkInk plans, explains and reviews your assignments — honestly and privately. We never write a word you could submit.</p>
        </div>
        <p className="text-sm text-white/70">UK &amp; Canada university assignment suite</p>
      </div>

      <div className="flex w-full items-center justify-center px-4 py-10 lg:w-7/12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="lg:hidden"><Logo /></div>
          <h1 className="mt-6 font-display text-3xl font-bold">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "login" ? "Log in to your ThinkInk workspace." : "Start with 20 free tokens. No card needed."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="auth-name" value={form.name} onChange={set("name")} required className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email" value={form.email} onChange={set("email")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password" value={form.password} onChange={set("password")} required minLength={mode === "register" ? 10 : undefined} className="mt-1.5" />
              {mode === "register" && <p className="mt-1 text-xs text-muted-foreground">At least 10 characters.</p>}
            </div>

            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="dob">Date of birth</Label>
                    <Input id="dob" type="date" data-testid="auth-dob" value={form.dob} onChange={set("dob")} required className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Select value={form.country} onValueChange={set("country")}>
                      <SelectTrigger data-testid="auth-country" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.country === "Canada" && (
                  <div>
                    <Label htmlFor="province">Province</Label>
                    <Input id="province" data-testid="auth-province" value={form.province} onChange={set("province")} placeholder="e.g. Ontario" className="mt-1.5" />
                  </div>
                )}

                <div className="rounded-2xl border border-border bg-accent/50 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                    <ShieldCheck className="h-4 w-4 text-[#0B5D3B] dark:text-[#25D366]" /> Academic Integrity Pledge
                  </div>
                  <div className="space-y-3">
                    <label className="flex cursor-pointer items-start gap-3 text-sm">
                      <Checkbox checked={allAgreed} onCheckedChange={setAllAgreed} data-testid="consent-all" className="mt-0.5" />
                      <span>I confirm ThinkInk helps me understand and improve <strong>my own</strong> work and will <strong>not</strong> write assignments for me. I'll follow my institution's academic integrity rules, accept the <span className="font-medium text-foreground">Terms &amp; Privacy Policy</span>, and I'm 18 or over.</span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
                      <Checkbox checked={consents.marketing} onCheckedChange={setC("marketing")} data-testid="consent-marketing" className="mt-0.5" />
                      <span>Send me occasional study tips and product updates (optional).</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-[#0B5D3B] text-white hover:bg-[#08472D]">
              {loading ? "Please wait…" : mode === "login" ? "Log in" : "I Agree & Continue to Workspace"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New to ThinkInk? " : "Already have an account? "}
            <button data-testid="auth-toggle-mode" className="font-semibold text-[#0B5D3B] hover:underline dark:text-[#25D366]"
              onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
